import axios from "axios";
import API from "../api";

export const AuthService = {
  /**
   * Login user with email and password
   * @param {Object} credentials - { email, password }
   * @returns {Promise} - API response
   */
  async login(credentials) {
    try {
      const response = await axios.post(
        `${API.BASE_URL}/auth/login`,
        credentials,
        {
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
          },
        }
      );
      return response;
    } catch (error) {
      throw this.handleAuthError(error);
    }
  },

  /**
   * Register new user
   * @param {Object} userData - { first_name, last_name, email, password, password_confirmation, hotel_name }
   * @returns {Promise} - API response
   */
  async register(userData) {
    try {
      const response = await axios.post(
        `${API.BASE_URL}/auth/register`,
        userData,
        {
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
          },
        }
      );
      return response;
    } catch (error) {
      throw this.handleAuthError(error);
    }
  },

  /**
   * Request password reset
   * @param {string} email - User email address
   * @returns {Promise} - API response
   */
  async requestPasswordReset(email) {
    try {
      const response = await axios.post(
        `${API.BASE_URL}/auth/forgot-password`,
        { email },
        {
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
          },
        }
      );
      return response;
    } catch (error) {
      throw this.handleAuthError(error);
    }
  },

  /**
   * Reset password with token
   * @param {Object} data - { token, email, password, password_confirmation }
   * @returns {Promise} - API response
   */
  async resetPassword(data) {
    try {
      const response = await axios.post(
        `${API.BASE_URL}/auth/reset-password`,
        data,
        {
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
          },
        }
      );
      return response;
    } catch (error) {
      throw this.handleAuthError(error);
    }
  },

  /**
   * Validate reset token
   * @param {string} token - Reset token
   * @returns {Promise} - API response
   */
  async validateResetToken(token) {
    try {
      const response = await axios.get(
        `${API.BASE_URL}/auth/reset-password/${token}`,
        {
          headers: {
            Accept: "application/json",
          },
        }
      );
      return response;
    } catch (error) {
      throw this.handleAuthError(error);
    }
  },

  /**
   * Verify current auth token
   * @returns {Promise} - API response
   */
  async verifyToken() {
    try {
      const response = await axios.get(
        `${API.BASE_URL}/user`,
        API.RequestHeaders()
      );
      return response;
    } catch (error) {
      throw this.handleAuthError(error);
    }
  },

  /**
   * Logout user
   * @returns {Promise} - API response
   */
  async logout() {
    try {
      const response = await axios.post(
        `${API.BASE_URL}/auth/logout`,
        {},
        API.RequestHeaders()
      );
      return response;
    } catch (error) {
      throw this.handleAuthError(error);
    }
  },

  /**
   * Handle authentication errors
   * @param {Object} error - Axios error object
   * @returns {Object} - Formatted error
   */
  handleAuthError(error) {
    if (error.response) {
      // Server responded with error status
      const { status, data } = error.response;

      switch (status) {
        case 422:
          // Validation errors
          return {
            message: data.message || "Validation failed",
            errors: data.errors || {},
            status,
          };
        case 401:
          // Unauthorized
          return {
            message: data.message || "Invalid credentials",
            status,
          };
        case 429:
          // Rate limited
          return {
            message:
              data.message || "Too many attempts. Please try again later.",
            retryAfter: data.retry_after,
            status,
          };
        case 500:
          // Server error
          return {
            message: "Server error. Please try again later.",
            status,
          };
        default:
          return {
            message: data.message || "An error occurred",
            status,
          };
      }
    } else if (error.request) {
      // Network error
      return {
        message: "Network error. Please check your connection.",
        status: 0,
      };
    } else {
      // Other error
      return {
        message: "An unexpected error occurred.",
        status: 0,
      };
    }
  },
};

export default AuthService;
